import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { LoadingState } from '../components/LoadingSpinner';
import { useAccessApi, ProfileUpdateRequest, ProfileUpdateError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
    buildProfileUpdateRequest,
    extractRestrictedFieldsFromProfile,
    hasEditableProfileField,
    mergeRestrictedFieldsFromErrorMessage,
} from './ProfilePage.helpers';
import { ProfileEditForm, ProfileReadOnlyView } from './ProfilePageSections';
import { pageTitleClassName } from '../components/ui/typography';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { AdminToast } from '../components/admin/AdminToast';
import {
    clearAdminToastTimer,
    emptyAdminToast,
    type AdminToastState,
} from '../components/admin/adminToastTypes';

export function ProfilePage() {
    useDocumentTitle('Profile');
    const { getAuth0Profile, updateAuth0Profile } = useAccessApi();
    const queryClient = useQueryClient();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<ProfileUpdateRequest>({});
    const [restrictedFields, setRestrictedFields] = useState<string[]>([]);
    const [toast, setToast] = useState<AdminToastState>(emptyAdminToast);
    const toastTimerRef = useRef<number | null>(null);
    const editButtonRef = useRef<HTMLButtonElement>(null);
    const wasEditing = useRef(false);

    useEffect(() => () => clearAdminToastTimer(toastTimerRef), []);

    useEffect(() => {
        if (wasEditing.current && !isEditing) {
            requestAnimationFrame(() => editButtonRef.current?.focus());
        }
        wasEditing.current = isEditing;
    }, [isEditing]);

    // Query for user profile
    const {
        data: profileData,
        isLoading,
        error,
        refetch: refetchProfile,
        isFetching: isFetchingProfile,
    } = useQuery({
        queryKey: ['auth0Profile'],
        queryFn: getAuth0Profile,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 2,
    });

    // Mutation for updating profile
    const updateProfileMutation = useMutation({
        mutationFn: updateAuth0Profile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['auth0Profile'] });
            setIsEditing(false);
            setFormData({});
            clearAdminToastTimer(toastTimerRef);
            setToast({ show: true, message: 'Profile updated.', type: 'success' });
            toastTimerRef.current = window.setTimeout(() => setToast(emptyAdminToast), 3000);
        },
        onError: (error) => {
            console.error('Profile update failed:', error);
            const errorMessage =
                error instanceof Error ? error.message : (error as ProfileUpdateError).error || '';
            setRestrictedFields((prev) =>
                mergeRestrictedFieldsFromErrorMessage(prev, errorMessage),
            );
        },
    });

    const profile = profileData?.profile;
    const effectiveRestrictedFields =
        restrictedFields.length > 0
            ? restrictedFields
            : extractRestrictedFieldsFromProfile(profile);

    useEffect(() => {
        if (!profile) {
            return;
        }

        setRestrictedFields((prev) =>
            prev.length > 0 ? prev : extractRestrictedFieldsFromProfile(profile),
        );
    }, [profile]);

    const handleEdit = () => {
        updateProfileMutation.reset();
        if (profile) {
            setFormData({
                given_name: profile.given_name || '',
                family_name: profile.family_name || '',
                name: profile.name || '',
                email: profile.email || '',
                picture: profile.picture || '',
                user_metadata: profile.user_metadata || {},
            });
        }
        setIsEditing(true);
    };

    const handleCancel = () => {
        updateProfileMutation.reset();
        setIsEditing(false);
        setFormData({});
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const updates = buildProfileUpdateRequest({
            formData,
            profile,
            restrictedFields,
        });

        // Only update if we have changes
        if (Object.keys(updates).length > 0) {
            updateProfileMutation.mutate(updates);
        } else {
            setIsEditing(false);
        }
    };

    const handleInputChange = (field: keyof ProfileUpdateRequest, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    if (isLoading) {
        return (
            <div className="mx-auto w-full max-w-4xl">
                <Card>
                    <LoadingState message="Loading profile…" />
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mx-auto w-full max-w-4xl">
                <Card>
                    <div className="p-4 sm:p-6">
                        <div role="alert" className="flex items-start gap-3">
                            <AlertCircle
                                className="mt-0.5 size-5 shrink-0 text-red-600"
                                aria-hidden
                            />
                            <div className="min-w-0">
                                <h1 className="text-base font-semibold text-neutral-900">
                                    Couldn't load your profile
                                </h1>
                                <p className="mt-1 break-words text-sm text-neutral-500">
                                    {error instanceof Error
                                        ? error.message
                                        : 'Unknown error occurred'}
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="mt-3"
                                    onClick={() => void refetchProfile()}
                                    disabled={isFetchingProfile}
                                >
                                    {isFetchingProfile ? 'Trying again…' : 'Try again'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="mx-auto w-full max-w-4xl">
                <Card>
                    <div className="p-4 text-left text-neutral-600 sm:p-6">Profile not found</div>
                </Card>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full max-w-4xl">
            <Card>
                {/* Header */}
                <div className="border-b border-neutral-200 px-4 py-4 sm:px-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className={pageTitleClassName}>Profile</h1>
                            <p className="text-sm text-neutral-600 mt-1">
                                Manage your personal information and account settings
                            </p>
                        </div>
                        {!isEditing && hasEditableProfileField(effectiveRestrictedFields) && (
                            <Button
                                ref={editButtonRef}
                                onClick={handleEdit}
                                variant="outline"
                                className="self-start sm:self-auto"
                            >
                                Edit profile
                            </Button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6">
                    {isEditing ? (
                        <ProfileEditForm
                            formData={formData}
                            restrictedFields={restrictedFields}
                            isPending={updateProfileMutation.isPending}
                            error={
                                updateProfileMutation.error instanceof Error
                                    ? updateProfileMutation.error
                                    : null
                            }
                            onInputChange={handleInputChange}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                        />
                    ) : (
                        <ProfileReadOnlyView
                            profile={profile}
                            restrictedFields={effectiveRestrictedFields}
                        />
                    )}
                </div>
            </Card>
            <AdminToast
                toast={toast}
                onClose={() => {
                    clearAdminToastTimer(toastTimerRef);
                    setToast(emptyAdminToast);
                }}
            />
        </div>
    );
}
